import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Badge, Button, Select, Switch } from '@artosera/ui';
import type { VueConnecteur } from '../../api/connecteurs.js';
import {
  useUnFlux,
  useApercuFlux,
  useModifierFlux,
  useSynchroniserFlux,
  type PlateformeDiffusion,
} from '../../api/diffusion.js';
import { ObjetCartel } from '../../composants/ObjetCartel.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { LienFluxDialog } from './LienFluxDialog.js';
import { Field } from '../Field.js';
import styles from './DiffusionPage.module.css';

export function CarteFlux({
  galerie,
  plateforme,
  connecteur,
  peutGerer,
  peutEcrire,
}: {
  galerie: string;
  plateforme: PlateformeDiffusion;
  connecteur: VueConnecteur | undefined;
  peutGerer: boolean;
  peutEcrire: boolean;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const lecture = useUnFlux(galerie, plateforme);
  const apercu = useApercuFlux(galerie, plateforme);
  const modifier = useModifierFlux(galerie, plateforme);
  const synchro = useSynchroniserFlux(galerie, plateforme);
  const nom = plateforme === 'artsy' ? 'Artsy' : 'Artnet';
  if (lecture.isPending) return <p role="status">{t('commun:chargement')}</p>;
  if (!lecture.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  const { flux, compteurs } = lecture.data;
  const mode = connecteur?.parametres['depot'] === 'url' ? 'url' : 'fichier';
  return (
    <article
      className={styles.carte}
      data-ui="diffusion-feed"
      aria-labelledby={`diffusion-${plateforme}`}
    >
      <div className={styles.entete}>
        <h2 id={`diffusion-${plateforme}`}>{nom}</h2>
        <Badge tone={flux.actif ? 'available' : 'neutral'}>
          {t(flux.actif ? 'regie:diffusion.ouvert' : 'regie:diffusion.ferme')}
        </Badge>
      </div>
      {!connecteur?.configure ? (
        <p>
          {t('regie:diffusion.connecteur.absent')}{' '}
          <Link to={`/${galerie}/reglages/connecteurs`}>
            {t('regie:diffusion.connecteur.regler')}
          </Link>
        </p>
      ) : (
        <p className={styles.sousTitre}>
          {t(`regie:diffusion.modes.${mode}`, { plateforme: nom })}
        </p>
      )}
      {connecteur?.mode === 'reel' ? (
        <p className={styles.sousTitre}>{t('regie:diffusion.limiteReelle')}</p>
      ) : null}
      <div className={styles.reglages}>
        <Switch
          id={`diffuser-${plateforme}`}
          label={t('regie:diffusion.diffuser')}
          checked={flux.actif}
          disabled={!peutGerer || modifier.isPending || !connecteur?.configure}
          onCheckedChange={(actif) => modifier.mutate({ revision: flux.revision, actif })}
        />
        <Switch
          id={`toutes-${plateforme}`}
          label={t('regie:diffusion.toutes')}
          checked={flux.toutesLesEligibles}
          disabled={!peutGerer || modifier.isPending}
          onCheckedChange={(toutesLesEligibles) =>
            modifier.mutate({ revision: flux.revision, toutesLesEligibles })
          }
        />
        <Field htmlFor={`langue-${plateforme}`} label={t('regie:diffusion.langue')}>
          <Select
            id={`langue-${plateforme}`}
            value={flux.langue}
            options={[
              { value: 'en', label: 'English' },
              { value: 'fr', label: 'Français' },
            ]}
            disabled={!peutGerer || modifier.isPending}
            onValueChange={(value) =>
              modifier.mutate({ revision: flux.revision, langue: value === 'fr' ? 'fr' : 'en' })
            }
          />
        </Field>
      </div>
      <div className={styles.compteurs} aria-label={t('regie:diffusion.rapport')}>
        {(['diffusee', 'refusee', 'retiree'] as const).map((etat) => (
          <span key={etat}>
            {t(`regie:diffusion.compteurs.${etat}`, { count: compteurs[etat] ?? 0 })}
          </span>
        ))}
      </div>
      {flux.dernierDepot ? (
        <p className={styles.sousTitre}>
          {t('regie:diffusion.dernierDepot', {
            date: new Date(flux.dernierDepot.deposeLe).toLocaleString(
              i18n.resolvedLanguage === 'en' ? 'en-GB' : 'fr-FR',
            ),
            count: flux.dernierDepot.nombre,
          })}{' '}
          — {t(`regie:diffusion.depôts.${flux.dernierDepot.statut}`)}
        </p>
      ) : null}
      {flux.derniereErreur ? <p role="alert">{t('regie:diffusion.echecDepot')}</p> : null}
      <div className={styles.actions}>
        {peutEcrire ? (
          <Button
            variant="secondary"
            disabled={synchro.isPending || !connecteur?.configure}
            onClick={() => synchro.mutate()}
          >
            {t('regie:diffusion.synchroniser')}
          </Button>
        ) : null}
        {peutGerer && mode === 'url' ? (
          <LienFluxDialog galerie={galerie} plateforme={plateforme} />
        ) : null}
      </div>
      {modifier.isError || synchro.isError || apercu.isError ? (
        <p role="alert">{t('commun:erreurReseau')}</p>
      ) : null}
      {synchro.isSuccess ? <p role="status">{t('regie:diffusion.synchroDemandee')}</p> : null}
      {apercu.data?.refusees.length ? (
        <section aria-labelledby={`refus-${plateforme}`}>
          <h3 id={`refus-${plateforme}`}>{t('regie:diffusion.aCompleter')}</h3>
          <ul className={styles.refusees}>
            {apercu.data.refusees.map((oeuvre) => (
              <li key={oeuvre.oeuvreId}>
                <VignetteOeuvre url={null} />
                <div>
                  <Link to={`/${galerie}/oeuvres/${oeuvre.oeuvreId}`}>
                    <ObjetCartel titre={oeuvre.titre} />
                  </Link>
                  <p>
                    {oeuvre.motifs.map((m) => t(`regie:diffusion.motifs.${m.code}`)).join(', ')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {flux.dernierDepot?.rejets.length ? (
        <section>
          <h3>{t('regie:diffusion.rejets')}</h3>
          <ul>
            {flux.dernierDepot.rejets.map((rejet) => (
              <li key={rejet.reference}>
                {rejet.reference} : {rejet.motif}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
